// TeamSlot.js — one row in the starters/bench list.
//
// Replaces the old chevron-expand action panel entirely. Every action gets
// its own affordance instead of one catch-all menu:
//   - promote/demote/reorder -> drag the row (long-press to pick up on
//     touch, see the TouchSensor delay in WeeklyLineupManager)
//   - start / bench / captain -> the slot pill on the left opens a sheet of
//                                every team it can swap with (ESPN's pattern).
//                                Captaincy is a swap into the captain slot,
//                                so there is no separate crown toggle.
//   - 5x Play                -> one-tap lightning icon, captain's row only:
//                                the 5x always sits on the captain
//   - freeze                 -> a button that only appears for starters, once
//                                the game is live and past halftime
//   - cut                    -> swipe the row left to reveal Cut
//   - team detail            -> tap the logo or the team name opens
//                                TeamDrawer. Long-press still starts a drag:
//                                the TouchSensor delay means a tap never
//                                becomes one.
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, Zap, Snowflake, Plus, ChevronDown } from 'lucide-react';
import { weeklyLineupUtils } from '../../utils/weeklyLineupUtils';
import {
  isTeamLocked,
  getGameDisplayInfo,
  getGameSpreadDisplay,
  getTeamSpread,
  isSuperDog,
  calculateCombinedPoints,
  shouldShowSpread,
} from '../../utils/gameStatus';
import { getWeeklyPoints } from '../../utils/teamStats';
import { SUPER_DOG_MARK, SUPER_DOG_LEGEND } from '../../utils/superDog';
import { explainTeamFantasyPoints, describeMultiplier } from '../../utils/scoreBreakdown';
import FreezeDetail from './FreezeDetail';

const CUT_REVEAL_PX = -76;

// Leading pill: what the slot is, and the way to change it.
const PILL_STYLE = {
  captain: { color: 'var(--color-captain-deep)', border: 'var(--color-captain-border)', background: 'var(--color-captain-fill)' },
  starters: { color: 'var(--color-brand)', border: 'var(--color-brand-border)', background: 'var(--color-bg-card)' },
  bench: { color: 'var(--color-text-muted)', border: 'var(--color-border)', background: 'var(--color-bg-card)' },
};

function SlotPill({ kind, label, disabled, onPress }) {
  const tone = PILL_STYLE[kind] || PILL_STYLE.bench;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onPress(); }}
      disabled={disabled}
      aria-label={`Swap this team, currently ${label}`}
      style={{
        minWidth: 46, minHeight: 28, padding: '0 8px', borderRadius: 999,
        border: `1.5px solid ${tone.border}`, backgroundColor: tone.background,
        color: tone.color, fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1,
      }}
    >
      {label}
    </button>
  );
}

const TeamSlot = ({
  team,
  section,
  index,
  rowIndex,
  size = 32,
  week,
  canEdit,
  isSaving,
  captain,
  tripPlayTeam,
  frozenTeams = [],
  frozenPoints = {},
  // This manager's freezes this week by team slug, each with the snapshot it
  // was written with. See utils/freezeDetail.js.
  freezeDetailBySlug = {},
  freezesRemaining = 0,
  TeamLogo,
  onOpenTeam,
  leagueId,
  gamesBySlug = {},
  weeklyPointsBySlug = {},
  ownerBySlug = {},
  allTeams,
  onTripPlayToggle,
  onSwapRequest,
  justMoved = false,
  onFreezeRequest,
  readOnly = false,
  onCutRequest,
}) => {
  const [swipeX, setSwipeX] = useState(0);
  // Whether the "how was this score made" panel is open. Per row, not
  // shared: closes on its own next render if the row unmounts (a swap or a
  // cut), never carries over onto a different team in the same slot.
  const [showBreakdown, setShowBreakdown] = useState(false);
  const swipeOriginX = useRef(null);
  const swipeStartX = useRef(0);
  const rowRef = useRef(null);

  // Schedule/score/lock all come from gamesBySlug (built from the `games`
  // table by WeeklyLineupManager, one query for the whole week), not
  // per-row async lookups against team_season_stats.next_opponent, which
  // lags `games` until the espn.js cron writes it. See buildGamesBySlug in
  // gameStatus.js.
  const normalizedSlug = team ? weeklyLineupUtils.normalizeTeamName(team) : null;
  const gameInfo = normalizedSlug ? gamesBySlug[normalizedSlug] : null;
  const gameDisplayInfo = team ? getGameDisplayInfo(gameInfo, normalizedSlug, allTeams) : null;
  if (gameDisplayInfo && gameInfo) {
    gameDisplayInfo.gameSpread = getGameSpreadDisplay(gameInfo, normalizedSlug);
    gameDisplayInfo.isSuperDog = isSuperDog(gameInfo, normalizedSlug);
  }
  const lockStatus = team ? isTeamLocked(gameInfo, normalizedSlug) : { locked: false };

  const handlePointerDown = (e) => {
    if (!canEdit || !team) return;
    swipeOriginX.current = e.clientX;
    swipeStartX.current = swipeX;
  };
  const handlePointerMove = (e) => {
    if (swipeOriginX.current == null) return;
    const delta = e.clientX - swipeOriginX.current;
    const next = swipeStartX.current + delta;
    setSwipeX(Math.min(0, Math.max(next, CUT_REVEAL_PX)));
  };
  const endSwipe = () => {
    if (swipeOriginX.current == null) return;
    swipeOriginX.current = null;
    setSwipeX((x) => (x < CUT_REVEAL_PX / 2 ? CUT_REVEAL_PX : 0));
  };

  // Tap anywhere outside an open row closes it, matching iOS swipe-to-delete.
  useEffect(() => {
    if (swipeX === 0) return undefined;
    const closeIfOutside = (e) => {
      if (rowRef.current && !rowRef.current.contains(e.target)) setSwipeX(0);
    };
    document.addEventListener('pointerdown', closeIfOutside);
    return () => document.removeEventListener('pointerdown', closeIfOutside);
  }, [swipeX]);

  if (!team) {
    return (
      <div
        className="flex items-center justify-center gap-3 py-5 min-h-[68px]"
        style={{ backgroundColor: justMoved ? 'var(--color-highlight)' : undefined, transition: 'background-color 0.4s' }}
      >
        {/* An empty slot is still a slot: it can take a team from anywhere
            else in the lineup, which is the same swap as any other. */}
        {canEdit && (
          <SlotPill kind={section === 'bench' ? 'bench' : 'starters'} label={section === 'bench' ? 'BE' : 'ST'} onPress={() => onSwapRequest({ section, index })} />
        )}
        {canEdit ? (
          <Link
            to={`/${leagueId}/teams?returnWeek=${week}&section=${section}&index=${index}`}
            className="flex items-center gap-2 no-underline"
            style={{ color: 'var(--color-brand)' }}
          >
            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-band-bg)' }}>
              <Plus size={16} strokeWidth={2.5} />
            </div>
            <span className="font-semibold text-sm">Add team</span>
          </Link>
        ) : (
          <div className="text-sm" style={{ color: 'var(--color-text-faint)' }}>Empty slot</div>
        )}
      </div>
    );
  }

  const normalizedTeamName = weeklyLineupUtils.normalizeTeamName(team);
  const isCaptain = captain === normalizedTeamName;
  const isTripPlay = tripPlayTeam === normalizedTeamName;
  const isFrozen = frozenTeams.includes(normalizedTeamName);
  const isLive = !!gameDisplayInfo?.isLive;
  const isSecondHalf = typeof gameDisplayInfo?.period === 'number' && gameDisplayInfo.period >= 3;
  // Starters only: freezing a bench team is a no-op since scoring.js only
  // reads frozen_teams for teams in `starters`, so the button shouldn't offer
  // it and burn a freeze on a team that can't score anyway.
  const canShowFreeze = !readOnly && section === 'starters' && isLive && isSecondHalf && !isFrozen;
  // hasStarted is only reported for an interrupted game, where the type alone
  // cannot say whether any football has been played: a game delayed before
  // kickoff has no score and must show a dash, not a zero.
  const gameStarted = gameDisplayInfo
    && gameDisplayInfo.type !== 'upcoming'
    && gameDisplayInfo.type !== 'bye-week'
    && gameDisplayInfo.hasStarted !== false;
  // Prefer the map WeeklyLineupManager refetches, and fall back to the boot
  // time AuthContext copy when a caller does not supply one. A live game's
  // points change every couple of minutes, so the boot copy goes stale within
  // a quarter.
  const freshPoints = weeklyPointsBySlug?.[normalizedSlug];
  const baseWeeklyPts = freshPoints
    ? getWeeklyPoints({ weeklyPoints: freshPoints }, week)
    : getWeeklyPoints(team.currentSeason, week);
  const weeklyPts = gameStarted ? calculateCombinedPoints(baseWeeklyPts, isCaptain, isTripPlay) : null;
  // A frozen team shows the score it was frozen at, which the scorer keeps in
  // the lineup's team_points with the captain or 5x multiplier already
  // applied. Recomputing from the team's weekly points showed where the game
  // finished instead, so a captain frozen at +20 read -4 while the week total
  // above it still counted the 20.
  const storedFrozen = isFrozen ? frozenPoints[normalizedTeamName] : undefined;
  const displayPoints = typeof storedFrozen === 'number' ? storedFrozen
    : gameStarted && weeklyPts != null ? weeklyPts.finalPoints
    : null;

  // How that number was made: only when there is a real score to explain and
  // it is not frozen. A frozen score was captured mid-game and kept exactly
  // as it stood; the game keeps playing underneath it, so rebuilding a
  // breakdown from the current score could describe a different number than
  // the one actually on screen. isFrozen already gets its own note below
  // instead.
  const canExplainScore = gameStarted && !isFrozen && weeklyPts != null && gameInfo;
  const scoreBreakdown = canExplainScore
    ? explainTeamFantasyPoints({
        won: gameInfo.homeScore > gameInfo.awayScore,
        tied: gameInfo.homeScore === gameInfo.awayScore,
        spread: getTeamSpread(gameInfo, normalizedSlug),
        actualMargin: gameInfo.homeScore - gameInfo.awayScore,
        isLive,
      })
    : null;
  const multiplier = describeMultiplier({ isCaptain, isTripPlay });

  // Every game gets the same two lines under the team name: who it is against,
  // then its status (kickoff time, live score, final score). Live and final
  // games used to join both onto one line, which clipped the clock off a long
  // opponent name ("vs Central Connecticut LIVE · 0-0 T Q1 ...") and left
  // those rows a line shorter than upcoming ones, so row heights jumped as
  // games kicked off. A bye or unscheduled week has no second line of its own
  // and renders a blank one to keep the height.
  //
  // The opponent carries its manager in brackets when a team in this league
  // holds it, and nothing when it is a free agent: most opponents are, so an
  // "(FA)" on most rows would be noise that costs width on a line that clips.
  // LIVE moves to the status line for the same reason, where the score it
  // qualifies sits.
  const opponentSlug = gameInfo
    ? (gameInfo.homeTeam === normalizedSlug ? gameInfo.awayTeam : gameInfo.homeTeam)
    : null;
  const opponentOwner = opponentSlug ? ownerBySlug[opponentSlug] : null;
  const matchup = gameDisplayInfo?.matchup || gameDisplayInfo?.subtext;
  const stateLine = !gameDisplayInfo ? 'No game scheduled'
    : gameDisplayInfo.type === 'bye-week' ? 'Bye week'
    : matchup;
  const statusLine = !gameDisplayInfo || gameDisplayInfo.type === 'bye-week' ? null
    : gameDisplayInfo.isLive ? `LIVE · ${gameDisplayInfo.display}`
    : gameDisplayInfo.display;

  // Pre-game, in-game and post-game. See shouldShowSpread for why, and so
  // that the rule has one home rather than being restated per row component.
  const showSpread = shouldShowSpread(gameDisplayInfo);
  const spread = showSpread ? gameDisplayInfo.gameSpread : null;
  // Marked here as well as on the home page: this is the screen where the rule
  // changes a decision, since a Super Dog costs nothing to start and lose with.
  const superDog = showSpread && gameDisplayInfo.isSuperDog;

  // Neutral zebra striping, not per-state row color: captain and locked
  // are already signaled by the crown fill and the small badges on the logo,
  // a full-row tint on top reads as noisy next to a reference like ESPN's
  // neutral list. The 5x Play is the one deliberate exception: it is spent
  // once for the entire season, not toggled week to week like the others, so
  // a manager scanning the lineup should be able to spot it without reading
  // every icon.
  // Frozen is the second exception, and it outranks the 5x tint: it is the
  // one state where the score on the row stops tracking the game beside it,
  // so it has to be visible without decoding a badge. Same icy tint and chip
  // as ReadOnlyTeamRow on the Scoreboard tab.
  // Striping follows render order, not slot order: the captain is drawn
  // elsewhere, so its slot is skipped and the array index has a hole in it.
  // Falls back to index for callers that render every row, like the bench.
  //
  // The alternate is --color-bg-row-alt rather than the page ground. On white
  // the two were a hair apart and read as zebra striping; in dark the page is
  // pure black against a #1C1C1E card, which is a big enough step that the
  // stripe looked like a state the row was in rather than decoration.
  const stripe = (rowIndex ?? index) % 2 === 0 ? 'var(--color-bg-card)' : 'var(--color-bg-row-alt)';
  const rowBg = isFrozen ? 'var(--color-freeze-bg)' : isTripPlay ? 'var(--color-tripplay-bg)' : stripe;

  return (
    <div ref={rowRef} style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Cut, revealed behind the row on swipe */}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={() => { onCutRequest(team, section, index); setSwipeX(0); }}
          style={{ width: -CUT_REVEAL_PX, backgroundColor: 'var(--color-danger)', color: 'var(--color-bg-card)', border: 'none', fontWeight: 700, fontSize: 13 }}
        >
          Cut
        </button>
      </div>

      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endSwipe}
        onPointerCancel={endSwipe}
        style={{
          position: 'relative',
          backgroundColor: justMoved ? 'var(--color-highlight)' : rowBg,
          // Left accent stripe via boxShadow rather than a real border, so it
          // costs no layout width and every row keeps the same padding. A
          // frozen row adds a frost ring in the same property, closing the
          // row off on all four sides. Both, in order, when a frozen team is
          // also the 5x.
          boxShadow: [
            isTripPlay ? 'inset 3px 0 0 0 var(--color-tripplay)' : null,
            isFrozen ? 'inset 0 0 0 1px var(--color-freeze-border)' : null,
          ].filter(Boolean).join(', ') || undefined,
          transform: swipeX ? `translateX(${swipeX}px)` : undefined,
          transition: swipeOriginX.current == null ? 'transform 0.2s, background-color 0.4s' : 'none',
          touchAction: 'pan-y',
        }}
        className="px-3 py-2"
      >
        <div className="grid grid-cols-[46px,36px,1fr,auto] gap-2 items-center" style={{ position: 'relative' }}>
          {/* Slot pill, then the 5x on the captain's row only */}
          <div className="flex flex-col items-center justify-center gap-1">
            <SlotPill
              kind={isCaptain ? 'captain' : section}
              label={isCaptain ? 'CAPT' : section === 'bench' ? 'BE' : 'ST'}
              disabled={!canEdit || lockStatus.locked}
              onPress={() => onSwapRequest({ section, index })}
            />
            {section === 'starters' ? (
              <>
                {/* 5x Play, captain's row only. It can sit nowhere else
                    (migration 032 enforces trip_play_team = captain), so on
                    any other starter the icon only ever produced a refusal. */}
                {isCaptain && (
                <button
                  onClick={(e) => { e.stopPropagation(); onTripPlayToggle(team); }}
                  disabled={!canEdit || lockStatus.locked}
                  aria-label={isTripPlay ? 'Remove 5x Play' : 'Use 5x Play'}
                  style={{ background: 'none', border: 'none', padding: 3, color: isTripPlay ? 'var(--color-tripplay)' : 'var(--color-text-faint)' }}
                >
                  <Zap size={14} fill={isTripPlay ? 'var(--color-tripplay)' : 'none'} />
                </button>
                )}
              </>
            ) : null}
          </div>

          {/* Logo */}
          <div className="relative">
            <TeamLogo teamName={team.school} size={size} clickable={true} />
            {isFrozen && (
              <div className="absolute -bottom-1 -left-1 rounded-full p-1" style={{ backgroundColor: 'var(--color-freeze)' }}>
                <Snowflake size={10} className="text-white" />
              </div>
            )}
            {lockStatus.locked && !isFrozen && (
              <div className="absolute -bottom-1 -left-1 rounded-full p-1" style={{ backgroundColor: 'var(--color-danger)' }}>
                <Lock size={10} className="text-white" />
              </div>
            )}
            {isLive && (
              <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full animate-pulse" style={{ backgroundColor: 'var(--color-success)' }} />
            )}
          </div>

          {/* Team + this-week state. Tapping opens the team drawer, the same
              as tapping the logo: the name is the bigger target and the one
              people reach for. Guarded on swipe so finishing a cut gesture
              over this block does not also open the sheet. */}
          <div
            className="min-w-0"
            onClick={() => {
              if (Math.abs(swipeX) > 4) return;
              onOpenTeam?.(team.school);
            }}
            style={{ cursor: onOpenTeam ? 'pointer' : 'default' }}
          >
            <div className="font-semibold text-[15px] leading-tight truncate flex items-center gap-1" style={{ color: lockStatus.locked ? 'var(--color-danger)' : 'var(--color-text-strong)' }}>
              <span className="truncate">{team.school}</span>
              {/* Chip, not plain text: matches ReadOnlyTeamRow on the
                  Scoreboard tab. Tinted by which side of the line the team is
                  on, so favourite and underdog are distinguishable without
                  reading the sign. */}
              {spread && (
                <span
                  className="flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold"
                  style={
                    spread === 'PICK'
                      ? { backgroundColor: 'var(--color-bg-page)', color: 'var(--color-text-muted)' }
                      : spread.startsWith('-')
                        ? { backgroundColor: 'var(--color-band-bg)', color: 'var(--color-brand)' }
                        : { backgroundColor: 'var(--color-captain-fill)', color: 'var(--color-captain-text)' }
                  }
                  title={
                    spread === 'PICK'
                      ? 'No favourite'
                      : spread.startsWith('-')
                        ? `Favoured by ${spread.slice(1)}`
                        : superDog
                          ? `Underdog by ${spread.slice(1)}. ${SUPER_DOG_LEGEND}`
                          : `Underdog by ${spread.slice(1)}`
                  }
                >
                  {spread}
                  {superDog && <span aria-label="Super Dog"> {SUPER_DOG_MARK}</span>}
                </span>
              )}
            </div>
            <div className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
              {/* The label lives on this line, not beside the team name: the
                  name here already shares its line with the slot pill, the
                  logo and the spread chip, and a chip up there cost a long
                  school name several characters. This line has the room, and
                  the icicles and the tint carry the state at a glance
                  anyway. */}
              {isFrozen && (
                <>
                  <span style={{ color: 'var(--color-freeze-text)', fontWeight: 700 }}>
                    Frozen at {displayPoints}
                  </span>
                  {' · '}
                </>
              )}
              {stateLine}
              {opponentOwner && gameDisplayInfo?.type !== 'bye-week' && (
                <>
                  {' ('}
                  {/* That manager's card on the Scoreboard, for the same week.
                      stopPropagation keeps the tap from also reaching the
                      row's drag and swipe handlers. */}
                  <Link
                    to={`/${leagueId}/scoreboard?member=${opponentOwner.memberId}&week=${week}`}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="no-underline"
                    style={{ color: 'var(--color-brand)' }}
                  >
                    {opponentOwner.name}
                  </Link>
                  {')'}
                </>
              )}
            </div>
            <div className="text-[11px] truncate" style={{ color: 'var(--color-text-muted)' }}>{statusLine || ' '}</div>
          </div>

          {/* Weekly points, and the freeze button beneath when it applies.
              The button used to replace the score rather than sit under it,
              which hid the one number the decision needs: freezing keeps the
              score as it stands, so being asked to commit without seeing it
              is the whole choice made blind. */}
          <div className="flex flex-col items-end gap-1">
            {scoreBreakdown || isFrozen ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowBreakdown((v) => !v); }}
                aria-expanded={showBreakdown}
                aria-label={`How ${team.school}'s score of ${displayPoints} was made`}
                className="flex items-center gap-0.5 font-bold text-sm"
                style={{
                  color: isFrozen ? 'var(--color-freeze)' : isCaptain ? 'var(--color-captain-deep)' : isTripPlay ? 'var(--color-tripplay)' : 'var(--color-brand)',
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                }}
              >
                {displayPoints}
                <ChevronDown size={12} style={{ transform: showBreakdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
            ) : (
              <div className="font-bold text-sm" style={{ color: isFrozen ? 'var(--color-freeze)' : isCaptain ? 'var(--color-captain-deep)' : isTripPlay ? 'var(--color-tripplay)' : 'var(--color-brand)' }}>
                {displayPoints ?? '—'}
              </div>
            )}
            {canShowFreeze && (
              <button
                onClick={(e) => { e.stopPropagation(); onFreezeRequest(team, weeklyPts?.finalPoints ?? 0); }}
                disabled={freezesRemaining <= 0}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-white text-[11px] font-semibold disabled:opacity-50"
                style={{ backgroundColor: 'var(--color-freeze)' }}
              >
                <Snowflake size={10} /> Freeze
              </button>
            )}
          </div>
        </div>

        {/* How the score above was made: a Win/Loss line, the spread cover or
            miss, Super Dog and the underdog bonus when they apply, then the
            captain/5x multiplier, in that order because that is the order
            calculateTeamFantasyPoints applies them in backend/scoring.js.
            Closed by default. Opens under the row it belongs to rather than
            a separate sheet, since it is one extra fact about a number
            already on screen, not a new place to go. */}
        {showBreakdown && (
          <div className="mt-2 pt-2 text-xs" style={{ borderTop: '1px solid var(--color-bg-subtle)' }} onClick={(e) => e.stopPropagation()}>
            {isFrozen ? (
              <FreezeDetail
                detail={freezeDetailBySlug[normalizedTeamName]}
                frozenPoints={displayPoints}
                currentPoints={gameStarted && weeklyPts != null ? weeklyPts.finalPoints : null}
                gameOver={gameDisplayInfo?.type === 'completed'}
                spread={getTeamSpread(gameInfo, normalizedTeamName)}
                isCaptain={isCaptain}
                isTripPlay={isTripPlay}
              />
            ) : scoreBreakdown && (
              <>
                {scoreBreakdown.lines.map((line, i) => (
                  <div key={i} className="flex justify-between py-0.5">
                    <span style={{ color: 'var(--color-text-muted)' }}>{line.label}</span>
                    <span style={{ fontWeight: 600, color: line.points < 0 ? 'var(--color-danger)' : line.points > 0 ? 'var(--color-success)' : 'var(--color-text-muted)' }}>
                      {line.points > 0 ? `+${line.points}` : line.points}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between py-0.5" style={{ borderTop: '1px solid var(--color-bg-subtle)', marginTop: 2, paddingTop: 4 }}>
                  <span style={{ color: 'var(--color-text-muted)' }}>Raw score</span>
                  <span style={{ fontWeight: 700 }}>{scoreBreakdown.total > 0 ? `+${scoreBreakdown.total}` : scoreBreakdown.total}</span>
                </div>
                {multiplier && (
                  <div className="flex justify-between py-0.5">
                    <span style={{ color: 'var(--color-text-muted)' }}>{multiplier.label}</span>
                    <span style={{ fontWeight: 600 }}>&times;{multiplier.factor}</span>
                  </div>
                )}
                {isLive && (
                  <p className="mt-1" style={{ color: 'var(--color-text-muted)', margin: '4px 0 0' }}>
                    Live. Updates as the game continues.
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamSlot;
